import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { ModelConfig } from '../../lib/model-icons';
import { ModelProviderIcon } from './ModelProviderIcon';

/** Rich catalog row: brand icon, title, description, badge, selected check. */
export function ModelRichRow({
  model,
  selected,
  name,
  muteBadge = false,
}: {
  model: ModelConfig;
  selected: boolean;
  /** Prefer a caller label (option.name) over the catalog name. */
  name?: string;
  /** Mute catalog badge colors (picker rows). ChatWelcome keeps `badgeClass`. */
  muteBadge?: boolean;
}) {
  return (
    <>
      <span className="flex shrink-0 items-center justify-center">
        <model.icon className={cn('h-4 w-4', !model.isBrandSvg && model.color)} />
      </span>
      <div className="min-w-0 flex-1">
        <span className="truncate text-sm font-medium">{name ?? model.name}</span>
        {model.description ? (
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{model.description}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {model.badge ? (
          <span
            className={cn(
              'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium leading-none',
              muteBadge ? 'bg-muted text-muted-foreground' : model.badgeClass,
            )}
          >
            {model.badge}
          </span>
        ) : null}
        {selected ? <Check className="h-3.5 w-3.5 shrink-0 text-primary" /> : null}
      </div>
    </>
  );
}

/** Fallback row for ids that are not in the chat catalog. */
export function ModelFallbackRow({
  id,
  name,
  title,
  selected,
  unavailableReason,
}: {
  id: string;
  name?: string;
  /** Full id for the native tooltip when the visible label is trimmed. */
  title?: string;
  selected: boolean;
  unavailableReason?: string;
}) {
  const label = name ?? id;
  return (
    <>
      <ModelProviderIcon model={id} />
      <span className="flex-1 truncate" title={title ?? label}>
        {label}
      </span>
      {unavailableReason ? (
        <span className="shrink-0 text-2xs text-muted-foreground">({unavailableReason})</span>
      ) : selected ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
      ) : null}
    </>
  );
}
